// configure the theme
import getMuiTheme from 'material-ui/styles/getMuiTheme'

import {
  amber200,
  amber50,
  amber700,
  blueGrey700,
  blueGrey900,
  cyan500,
  deepOrange50,
  deepOrange500,
  deepOrange700,
  green100,
  green50,
  green500,
  green700,
  grey400,
  grey500,
  grey600,
  grey700,
  grey800,
  grey900,
  indigo100,
  indigo50,
  indigo700,
  lightBlue700,
  pink500,
  purple700,
  red100,
  red50,
  red700,
} from 'material-ui/styles/colors'

// this is crude but will get the job done for now
//mostly used for mui 0.2 components please use thama/vars for any customer components we plan on removing this file
// eslint-disable-next-line no-unused-vars
import css from './index.scss'

import animations from './animations'

import icons from './icons'

import {createSizes} from './sizes'

import {getCssVariable} from 'helpers/css'

const minViewWidth = 1024
const maxViewWidth = 1400

/**
 * Get theme colors from CSS variables
 * This function reads colors from CSS custom properties, which automatically
 * handles light/dark mode switching via the [data-theme] attribute
 */
const getThemeColors = () => {
  return {
    // Text colors
    primaryFontColor: getCssVariable('--cg-color-text-primary', blueGrey900),
    darkTextColor: getCssVariable('--cg-color-text-primary', blueGrey900),
    lightTextColor: getCssVariable('--cg-color-text-light', '#888888'),
    disabledColor: getCssVariable('--cg-color-text-disabled', '#AAAAAA'),
    disabledTextColor: getCssVariable('--cg-color-text-disabled', '#AAAAAA'),

    // Icon colors
    primaryIconColor: getCssVariable('--cg-color-icon-primary', blueGrey900),
    lightIconColor: getCssVariable('--cg-color-icon-light', '#999999'),
    disabledIconColor: getCssVariable('--cg-color-icon-disabled', '#AAAAAA'),

    // Border colors
    borderColor: getCssVariable('--cg-color-border-default', '#CFD4D8'),
    lightBorderColor: getCssVariable('--cg-color-border-light', '#EEE'),

    // Background colors
    background1Color: getCssVariable('--cg-color-bg-default', '#f2f2f2'),
    background2Color: getCssVariable('--cg-color-bg-level2', '#eeeeee'),
    background3Color: getCssVariable('--cg-color-bg-level3', '#e8e8e8'),
    background4Color: getCssVariable('--cg-color-bg-level4', '#d8d8d8'),
    disabledBackgroundColor: getCssVariable('--cg-color-bg-disabled', '#F2F2F2'),
    hoverBackgroundColor: getCssVariable('--cg-color-bg-hover-light', '#EEEEEE'),

    whiteColor: getCssVariable('--cg-color-bg-white', '#FFFFFF'),

    // TextField specific colors
    textFieldFloatingLabelColor: getCssVariable('--cg-color-text-light', grey700),
    textFieldFloatingLabelFocusColor: getCssVariable('--cg-color-primary-main', deepOrange700),
    textFieldUnderlineColor: getCssVariable('--cg-color-border-default', '#CFD4D8'),
    textFieldInputColor: getCssVariable('--cg-color-text-primary', 'rgba(0, 0, 0, 0.87)'),
  }
}

/**
 * Create Material-UI theme
 * Colors are read from CSS variables which automatically handle light/dark mode
 * @param {string} [mode='light'] - Deprecated: mode is now handled by CSS variables via [data-theme] attribute
 */
export const createMuiTheme = () => {
  const colors = getThemeColors()

  return getMuiTheme({
    palette: {

      // Marketing brand/logo colours
      // Go: #FF5F00
      // Pro: #3952FE
      // Clarity: #02CE7C
      // Dark: #0F1A24

      textColor: colors.primaryFontColor,
      canvasColor: colors.whiteColor,
      alternateTextColor: colors.whiteColor,

      primary1Color: getCssVariable('--cg-color-primary-main', deepOrange700),
      accent1Color: getCssVariable('--cg-color-accent', deepOrange700),
      headerColor: getCssVariable('--cg-color-header-bg', blueGrey900),

      primary1HoverColor: getCssVariable('--cg-color-primary-hover', deepOrange500),
      primary1LightColor: getCssVariable('--cg-color-primary-light', deepOrange50),

      primary1HoverBackgroundColor: getCssVariable('--cg-color-primary-hover-bg', 'rgba(255,87,34,0.15)'),

      borderColor: colors.borderColor,
      lightBorderColor: colors.lightBorderColor,

      background1Color: colors.background1Color,
      background2Color: colors.background2Color,
      background3Color: colors.background3Color,
      background4Color: colors.background4Color,
      disabledBackgroundColor: colors.disabledBackgroundColor,
      hoverBackgroundColor: colors.hoverBackgroundColor,
      hoverHighlightColor: getCssVariable('--cg-color-header-hover', blueGrey700),

      primaryFontColor: colors.primaryFontColor,
      darkTextColor: colors.darkTextColor,
      lightTextColor: colors.lightTextColor,
      disabledColor: colors.disabledColor,
      disabledTextColor: colors.disabledTextColor,

      primaryIconColor: colors.primaryIconColor,
      lightIconColor: colors.lightIconColor,
      disabledIconColor: colors.disabledIconColor,

      proPurple: '#58276f',
      proPurpleBackground: 'rgba(104, 31, 128, 0.4)',
      proPink: '#e61a65',
      proPinkBackground: 'rgba(238, 27, 111, 0.4)',

      contrastBackgroundColor: getCssVariable('--cg-color-contrast-bg', grey800),
      contrastBorderColor: getCssVariable('--cg-color-contrast-border', grey900),
      contrastTextColor: getCssVariable('--cg-color-contrast-text', grey400),
      contrastIconColor: getCssVariable('--cg-color-contrast-icon', grey400),
      contrastHoverBackgroundColor: getCssVariable('--cg-color-contrast-hover-bg', grey600),
      contrastHoverTextColor: getCssVariable('--cg-color-contrast-text', grey400),
      contrastActiveBackgroundColor: getCssVariable('--cg-color-bg-white', '#fff'),
      contrastActiveTextColor: '#58276f',
      contrastActiveIconColor: '#58276f',

      goodColor: getCssVariable('--cg-color-success', green500),
      goodBackgroundColor: green50,
      goodBackground2Color: green100,

      infoColor: indigo700,
      infoBackgroundColor: indigo50,
      infoBackground2Color: indigo100,

      errorColor: getCssVariable('--cg-color-error', red700),
      errorBackgroundColor: red50,
      errorBackground2Color: red100,

      warningColor: getCssVariable('--cg-color-warning', amber700),
      warningBackgroundColor: amber50,
      warningBorderColor: amber200,

      whiteColor: colors.whiteColor,
      redColor: getCssVariable('--cg-color-error-bright', red700),
      blackColor: getCssVariable('--cg-color-header-text-active', '#020202'),

      signatureColor: 'black',

      contacts: {
        postedStatus: {
          notPosted: grey500,
          posted: green700,
          failedToPost: red700,
        },
        emailSendStatus: {
          notSent: grey500,
          ok: green700,
          softBounce: amber700,
          hardBounce: red700,
        },
        emailValidation: {
          valid: green700,
          invalid: red700,
        },
      },

      deliveryNoteStatus: {
        draft: grey500,
        draftReturn: red700,
        awaitingReturn: red700,
        returned: red700,
        scheduled: amber700,
        delivered: green700,
        collected: green700,
      },
      deliveryItemStatus: {
        pending: grey500,
        readyForDelivery: green700,
        scheduled: amber700,
        delivered: lightBlue700,
      },
      deliveryStatus: {
        unscheduled: getCssVariable('--cg-color-delivery-unscheduled', grey500),
        scheduled: getCssVariable('--cg-color-delivery-scheduled', indigo700),
        wip: getCssVariable('--cg-color-delivery-wip', amber700),
        onHold: getCssVariable('--cg-color-delivery-onhold', red700),
        complete: getCssVariable('--cg-color-delivery-complete', green700),
      },
      jobCardStatus: {
        pending: getCssVariable('--cg-color-jobboard-pending', indigo700),
        wip: getCssVariable('--cg-color-jobboard-wip', amber700),
        onHold: getCssVariable('--cg-color-jobboard-onhold', red700),
        complete: getCssVariable('--cg-color-jobboard-complete', green700),
        notScheduled: grey500,
        noProductionRequired: green700,
        delivered: lightBlue700,
      },
      opportunityStatus: {
        rejected: red700,
        won: green700,
        open: amber700,
      },
      productionStatus: {
        unscheduled: grey500,
        scheduled: indigo700,
        wip: amber700,
        onHold: red700,
        complete: green700,
      },
      quoteStatus: {
        draft: grey500,
        quoted: indigo700,
        accepted: cyan500,  // pseudo-status
        confirmed: amber700,
        partDelivered: lightBlue700,
        delivered: lightBlue700,
        inDelivery: lightBlue700,
        invoiced: purple700,
        credited: pink500,
        paid: green700,
        closed: grey700,
        rejected: red700,
        noSchedule: grey500,
      },
      approvalStatus: {
        approved: green700,
        changesRequested: purple700,
        draft: grey500,
        inReview: amber700,
        rejected: red700,
      },
      purchaseOrderStatus: {
        draft: grey500,
        issued: indigo700,
        partReceived: lightBlue700,
        received: lightBlue700,
        closed: grey700,
        cancelled: red700,
        confirmed: amber700,
        paid: green700,
      },
      invoiceType: {
        awaitingPosting: grey500,
        invoice: green500,
        creditNote: purple700,
      },

      pendingStatusColor: indigo700,
      inProgressStatusColor: amber700,
      onHoldStatusColor: red700,
      rejectStatusColor: red700,
      completedStatusColor: green700,
    },
    partStatus: {
      active: green700,
      buyOnly: indigo700,
      comingSoon: amber700,
      discontinued: red700,
    },
    // Used by custom Icon component
    iconColor: blueGrey900,
    icon: {
      backgroundColor: 'transparent',
      foregroundColor: 'white',
      internalRatio: 0.66,
      borderRadius: '4%',
      color: grey700,
      disabledColor: '#AAAAAA',
      size: createSizes(8, 12, 20, 20, 38, 68),
      margin: 4,
    },
    action: {
      size: createSizes(12, 24, 36, 36, 48, 60),
    },
    // pre-configured icons for specific uses
    icons: icons,
    // get the animations for easy access
    animations: animations,
    // define the typography
    type: {
      display4: {
        fontStyle: 112,
        fontWeight: 300,
        lineHeight: '128px',
        letterSpacing: -5,
      },
      display3: {
        fontSize: 56,
        fontWeight: 400,
        lineHeight: '64px',
      },
      display2: {
        fontSize: 45,
        fontWeight: 400,
        lineHeight: '64px',
      },
      display1: {
        fontSize: 34,
        fontWeight: 400,
        lineHeight: '52px',
      },
      headline: {
        fontSize: 24,
        fontWeight: 400,
        lineHeight: '44px',
      },
      title: {
        fontSize: 20,
        fontWeight: 600,
        lineHeight: '44px',
      },
      subhead: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '32px',
      },
      menu: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '32px',
      },
      button: {
        fontSize: 14,
        transform: 'uppercase',
        fontWeight: 600,
        lineHeight: '32px',
      },
      caption: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '32px',
      },
      body: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '25px',
      },
      entityTitle: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '25px',
        color: grey700,
      },
      entitySubtitle: {
        fontSize: 12,
        lineHeight: '8px',
        color: grey700,
      },
    },
    components: {
      textField: {
        floatingLabel: {
          color: colors.textFieldFloatingLabelColor,
        },
        floatingLabelFocus: {
          color: colors.textFieldFloatingLabelFocusColor,
        },
        underlineDisabledStyle: {
          borderBottom: `1px solid ${colors.textFieldUnderlineColor}`,
        },
        input: {
          color: colors.textFieldInputColor,
        },
        textarea: {
          color: colors.textFieldInputColor,
        },
      },
    },
    views: {
      contextScene: {
        // height: 'calc(100% - 110px)',
        // display: 'flex',
        // position: 'absolute',
        height: 'calc(100% - 132px)',
        width: '100%',
      },
      flexContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        minWidth: minViewWidth,
        width: '100%',
      },
      flexContainerInner: {
        margin: '0 auto',
        minWidth: minViewWidth,
        maxWidth: maxViewWidth,
        width: '80%',
      },
      flexContainerMaster: {
        maxWidth: 384,
        minWidth: 256,
        flex: '0 0 25%',
      },
      flexContainerDetail: {
        flexGrow: 1,
        minWidth: 768,
      },
    },
  })
}

// Default export - light theme for backward compatibility
export default createMuiTheme('light')
